// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import LoadingSpinner from '../components/LoadingSpinner';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [collegeName, setCollegeName] = useState('College Admin');
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'college_info');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          if (data.collegeName) setCollegeName(data.collegeName);
          if (data.logoUrl) setLogoUrl(data.logoUrl);
        }
      } catch (err) {
        console.warn('Error loading settings:', err);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/dashboard');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      let errorMessage = 'Login failed. Please check your credentials.';
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = 'Invalid email format';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        default:
          errorMessage = error.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    .login-page {
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow-x: hidden;
    }

    .bg-animation {
      position: fixed;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      z-index: 0;
      overflow: hidden;
    }

    .bubble {
      position: absolute;
      bottom: -100px;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      animation: rise 10s infinite ease-in;
    }

    .bubble:nth-child(1) { width: 80px; height: 80px; left: 10%; animation-duration: 8s; }
    .bubble:nth-child(2) { width: 120px; height: 120px; left: 25%; animation-duration: 12s; animation-delay: 2s; }
    .bubble:nth-child(3) { width: 60px; height: 60px; left: 45%; animation-duration: 10s; animation-delay: 1s; }
    .bubble:nth-child(4) { width: 100px; height: 100px; left: 70%; animation-duration: 14s; animation-delay: 3s; }
    .bubble:nth-child(5) { width: 50px; height: 50px; left: 85%; animation-duration: 9s; animation-delay: 0.5s; }
    .bubble:nth-child(6) { width: 150px; height: 150px; left: 55%; animation-duration: 16s; animation-delay: 4s; }
    .bubble:nth-child(7) { width: 70px; height: 70px; left: 15%; animation-duration: 11s; animation-delay: 1.5s; }
    .bubble:nth-child(8) { width: 90px; height: 90px; left: 35%; animation-duration: 13s; animation-delay: 2.5s; }

    @keyframes rise {
      0% { bottom: -100px; transform: translateX(0); opacity: 0; }
      50% { opacity: 0.5; }
      100% { bottom: 100vh; transform: translateX(-100px); opacity: 0; }
    }

    .login-container {
      position: relative;
      z-index: 1;
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px);
      border-radius: 32px;
      padding: 48px 40px;
      width: 90%;
      max-width: 460px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: fadeInUp 0.6s ease-out;
    }

    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .logo {
      text-align: center;
      margin-bottom: 36px;
    }

    .college-logo {
      width: 80px;
      height: 80px;
      object-fit: contain;
      margin-bottom: 16px;
      border-radius: 20px;
    }

    .logo-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #00d9ff, #0ea5e9);
      border-radius: 25px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
    }

    .logo-icon i {
      font-size: 48px;
      color: white;
    }

    .logo h1 {
      font-size: 28px;
      color: #081120;
      margin-bottom: 8px;
      font-weight: 700;
    }

    .logo p {
      color: #6b7280;
      font-size: 14px;
    }

    .college-name {
      font-size: 16px;
      color: #00d9ff;
      margin-top: 8px;
      font-weight: 600;
    }

    .demo-hint {
      background: #f0f9ff;
      border-radius: 12px;
      padding: 10px;
      margin-bottom: 20px;
      text-align: center;
      font-size: 12px;
      color: #0ea5e9;
    }

    .error-box {
      background: #fee2e2;
      border-left: 4px solid #ef4444;
      padding: 12px 16px;
      border-radius: 12px;
      margin-bottom: 20px;
      color: #dc2626;
      font-size: 14px;
      display: none;
      align-items: center;
      gap: 10px;
    }

    .error-box.show {
      display: flex;
    }

    .success-box {
      background: #dff9e6;
      border-left: 4px solid #10b981;
      padding: 12px 16px;
      border-radius: 12px;
      margin-bottom: 20px;
      color: #1f8b4c;
      font-size: 14px;
      display: none;
      align-items: center;
      gap: 10px;
    }

    .success-box.show {
      display: flex;
    }

    .input-group {
      display: flex;
      align-items: center;
      background: #f8fafc;
      border: 2px solid #e2e8f0;
      border-radius: 20px;
      padding: 4px 16px;
      margin-bottom: 20px;
      transition: all 0.3s;
    }

    .input-group:hover {
      border-color: #00d9ff;
      background: white;
    }

    .input-group:focus-within {
      border-color: #00d9ff;
      box-shadow: 0 0 0 3px rgba(0, 217, 255, 0.1);
      background: white;
    }

    .input-group i {
      color: #94a3b8;
      font-size: 18px;
      margin-right: 12px;
      transition: color 0.3s;
    }

    .input-group:focus-within i {
      color: #00d9ff;
    }

    .input-group input {
      flex: 1;
      border: none;
      background: transparent;
      padding: 14px 0;
      font-size: 15px;
      outline: none;
      color: #1e293b;
    }

    .input-group input::placeholder {
      color: #94a3b8;
    }

    .login-btn {
      width: 100%;
      background: linear-gradient(135deg, #00d9ff, #0ea5e9);
      color: white;
      border: none;
      padding: 14px;
      border-radius: 40px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s;
      margin-top: 10px;
      margin-bottom: 20px;
    }

    .login-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px -5px rgba(0, 217, 255, 0.4);
    }

    .login-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }

    .bottom-text {
      text-align: center;
      margin-top: 24px;
      padding-top: 20px;
      border-top: 1px solid #eef2ff;
      color: #94a3b8;
      font-size: 12px;
    }

    @media (max-width: 480px) {
      .login-container {
        padding: 32px 24px;
        margin: 20px;
      }
      .logo h1 {
        font-size: 24px;
      }
      .logo-icon, .college-logo {
        width: 60px;
        height: 60px;
      }
      .logo-icon i {
        font-size: 32px;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="login-page">
        <div className="bg-animation">
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
        </div>

        <div className="login-container">
          <div className="logo">
            <div className="logo-container">
              {logoUrl ? (
                <img src={logoUrl} alt="College Logo" className="college-logo" />
              ) : (
                <div className="logo-icon">
                  <i className="fa-solid fa-graduation-cap"></i>
                </div>
              )}
            </div>
            <h1>Admin Panel</h1>
            <p>College Website Management</p>
            <div className="college-name">{collegeName}</div>
          </div>

          <div className="demo-hint">
            <i className="fas fa-info-circle"></i> Demo: admin@college.edu / admin123
          </div>

          {error && (
            <div className="error-box show">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="success-box show">
              <i className="fas fa-check-circle"></i>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="input-group">
              <i className="fa-solid fa-envelope"></i>
              <input
                type="email"
                placeholder="Enter Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="input-group">
              <i className="fa-solid fa-lock"></i>
              <input
                type="password"
                placeholder="Enter Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'LOGGING IN...' : 'LOGIN'}
            </button>
          </form>

          {loading && <LoadingSpinner />}

          <div className="bottom-text">
            <i className="fas fa-shield-alt"></i>  Secure Authentication
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;